import {
  BinIcon,
  Button,
  KebabIcon,
  LinkedIcon,
  Menu,
  Popover,
  UnlinkIcon,
  useToast,
} from "@codat/orchard-ui";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { useState } from "react";
import { fileUploadIntegrationIds } from "utils/constants";

import { ConnectionStatus } from "pages/Companies/Companies";

import styles from "./ActionsCell.module.scss";

interface ActionsCellProps {
  value: {
    platformName: string;
    linkUrl: string;
    connectionStatus: ConnectionStatus;
    integrationId: string;
    onUnlink: () => void;
    onDelete: () => void;
  };
}

const ActionsCell = ({ value }: ActionsCellProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { addToast } = useToast();
  const { userState } = useUserState();

  const userCanEditConnection = userState?.userPermissions.includes(
    "clients_dataConnections_edit"
  );
  const userCanDeleteConnection = userState?.userPermissions.includes(
    "clients_dataConnections_delete"
  );

  const isFileUpload = fileUploadIntegrationIds.includes(value.integrationId);

  const isUnlinkable =
    ["Linked", "Deauthorized"].includes(value.connectionStatus) &&
    !isFileUpload;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value.linkUrl);
      addToast({
        message: "Link URL copied to clipboard",
        variant: "success",
      });
    } catch (e: unknown) {
      const err =
        e instanceof Error ? e : new Error("Error writing text to clipboard");
      addToast({
        message: err.message,
        variant: "error",
      });
    }
  };
  return (
    <div>
      <Popover
        popoverClassName={styles.popover}
        placement={[
          "right-start",
          "bottom-start",
          "bottom",
          "top-start",
          "left",
        ]}
        isOpen={isOpen}
        removePadding
        size="small"
        onClose={() => setIsOpen(false)}
        content={
          <Menu>
            <Menu.Item
              icon={<LinkedIcon />}
              label="Copy Link URL"
              id="copy-link-url"
              subtext={`Take the user to the ${value.platformName} section of their Link flow`}
              onClick={onCopy}
              data-testid="copy-link-url"
            />
            <Menu.Item
              icon={<UnlinkIcon />}
              label="Unlink connection"
              id="unlink-connection"
              subtext="Revoke access to this connection"
              onClick={value.onUnlink}
              isDisabled={!userCanEditConnection || !isUnlinkable}
              className={styles.destructiveAction}
              data-testid="unlink-connection"
            />
            <Menu.Item
              icon={<BinIcon />}
              label="Delete connection"
              id="delete-connection"
              subtext="This cannot be undone"
              onClick={value.onDelete}
              isDisabled={!userCanDeleteConnection}
              className={styles.destructiveAction}
              data-testid="delete-connection"
            />
          </Menu>
        }
      >
        <Button
          label="Actions"
          variant="secondary"
          color="interface"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="actions-button"
          className={styles.button}
        >
          <KebabIcon />
        </Button>
      </Popover>
    </div>
  );
};

export default ActionsCell;
